import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Badge, Button, Col, Form, Row, Spinner } from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import { GET_CAMPAIGNS, GET_MESSAGE_TEMPLATES } from '../../gql/queries/campaigns'
import {
  CREATE_CAMPAIGN,
  DUPLICATE_MESSAGE_TEMPLATE,
  UPDATE_CAMPAIGN,
} from '../../gql/mutations/campaigns'
import {
  CampaignChannel,
  ICampaign,
  ICampaignsRes,
  IMessageTemplatesRes,
} from '../../types/campaigns'
import { CampaignFormModal, CampaignFormValues } from './components/CampaignFormModal'
import { CampaignsNav } from './components/CampaignsNav'
import { ChannelBadge } from './components/ChannelBadge'

const statusBadgeVariant: Record<string, string> = {
  DRAFT: 'secondary',
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'info',
  FINISHED: 'info',
}

const statusBadgeText: Record<string, string> = {
  COMPLETED: 'white',
  FINISHED: 'white',
}

const formatDate = (value?: string | null) => {
  if (!value) return null
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

export const CampaignsPage = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [campaignModalOpen, setCampaignModalOpen] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<ICampaign | null>(null)

  const {
    data: campaignsData,
    loading: loadingCampaigns,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useQuery<ICampaignsRes>(GET_CAMPAIGNS, {
    variables: { includeArchived: false },
    fetchPolicy: 'cache-and-network',
  })

  const { data: templatesData, refetch: refetchTemplates } = useQuery<IMessageTemplatesRes>(
    GET_MESSAGE_TEMPLATES,
    { fetchPolicy: 'cache-and-network' }
  )

  const [createCampaignMutation, { loading: creatingCampaign }] = useMutation(CREATE_CAMPAIGN)
  const [updateCampaignMutation, { loading: updatingCampaign }] = useMutation(UPDATE_CAMPAIGN)
  const [duplicateTemplateMutation] = useMutation(DUPLICATE_MESSAGE_TEMPLATE)

  const campaigns = campaignsData?.campaigns ?? []
  const templates = templatesData?.messageTemplates ?? []

  const filtered = useMemo(() => {
    let list = campaigns
    if (statusFilter) list = list.filter((c) => c.status === statusFilter)
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      list = list.filter((c) =>
        [c.name, c.description ?? '', c.status, c.scheduleType].some((f) =>
          f?.toLowerCase().includes(term)
        )
      )
    }
    return list
  }, [campaigns, searchTerm, statusFilter])

  const handleSubmitCampaign = async (values: CampaignFormValues) => {
    try {
      if (editingCampaign) {
        await updateCampaignMutation({
          variables: {
            id: editingCampaign.id,
            name: values.name,
            description: values.description,
            enabledChannels: values.enabledChannels,
            strategy: values.strategy,
            scheduleType: values.scheduleType,
            scheduledTime: values.scheduledTime,
            recurringRule: values.recurringRule || null,
            targetSegment: values.targetSegment,
            templateId: values.templateId,
            metadata: values.metadata,
          },
        })
        toast.success('Campaign updated')
      } else {
        const { data } = await createCampaignMutation({
          variables: {
            name: values.name,
            description: values.description,
            enabledChannels: values.enabledChannels,
            strategy: values.strategy,
            scheduleType: values.scheduleType,
            scheduledTime: values.scheduledTime,
            recurringRule: values.recurringRule || null,
            targetSegment: values.targetSegment,
            templateId: values.templateId,
            metadata: values.metadata,
          },
        })
        toast.success('Campaign created')
        const newId = data?.createCampaign?.campaign?.id
        if (newId) navigate(`/campaigns/${newId}`)
      }
      setCampaignModalOpen(false)
      await refetchCampaigns()
    } catch {
      toast.error('There was a problem saving the campaign')
    }
  }

  const statuses = ['DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED']

  return (
    <div className='campaigns-page'>
      <CampaignsNav />

      {/* Page header */}
      <div className='d-flex align-items-center justify-content-between mb-6 flex-wrap gap-3'>
        <div>
          <h1 className='page-title mb-1'>Messaging campaigns</h1>
          <div className='text-muted'>Coordinate SMS, email, WhatsApp, and Telegram flows from one workspace.</div>
        </div>
        <Button variant='primary' onClick={() => { setEditingCampaign(null); setCampaignModalOpen(true) }}>
          <i className='bi bi-plus-lg me-2'></i>
          New campaign
        </Button>
      </div>

      {campaignsError && <Alert variant='danger'>Unable to load campaigns: {campaignsError.message}</Alert>}

      {/* Filters */}
      <div className='d-flex flex-wrap gap-3 mb-6 align-items-center'>
        <div style={{ maxWidth: 300 }} className='flex-grow-1'>
          <Form.Control
            type='search'
            placeholder='Search campaigns…'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className='d-flex gap-2 flex-wrap'>
          <Button
            size='sm'
            variant={statusFilter === '' ? 'primary' : 'light'}
            onClick={() => setStatusFilter('')}
          >
            All
          </Button>
          {statuses.map((s) => (
            <Button
              key={s}
              size='sm'
              variant={statusFilter === s ? 'primary' : 'light'}
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </Button>
          ))}
        </div>
        {loadingCampaigns && <Spinner animation='border' size='sm' />}
      </div>

      {/* Campaign grid */}
      {!loadingCampaigns && filtered.length === 0 ? (
        <div className='text-center text-muted py-20'>
          <i className='bi bi-megaphone fs-1 d-block mb-3'></i>
          {searchTerm || statusFilter ? 'No campaigns match your filters.' : 'No campaigns yet. Create your first one!'}
        </div>
      ) : (
        <Row className='g-5'>
          {filtered.map((campaign) => {
            const enabledChannels = Array.isArray(campaign.enabledChannels)
              ? campaign.enabledChannels
              : typeof campaign.enabledChannels === 'string'
              ? (campaign.enabledChannels as string).split(',').map((c) => c.trim()).filter(Boolean)
              : []
            const lastRun = formatDate(campaign.lastRunAt)
            const statusVariant = statusBadgeVariant[campaign.status] ?? 'secondary'
            const statusText = statusBadgeText[campaign.status]
            const deliveryRate =
              campaign.sentCount > 0
                ? Math.round((campaign.deliveredCount / campaign.sentCount) * 100)
                : null

            return (
              <Col key={campaign.id} xs={12} md={6} xl={4}>
                <div
                  className='card h-100 campaign-card'
                  role='button'
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)')}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '')}
                >
                  <div className='card-body d-flex flex-column gap-3'>
                    {/* Top row */}
                    <div className='d-flex justify-content-between align-items-start'>
                      <div className='flex-grow-1 me-3'>
                        <div className='fw-bold fs-5 mb-1'>{campaign.name}</div>
                        <div className='text-muted small text-truncate'>
                          {campaign.description || 'No description'}
                        </div>
                      </div>
                      <Badge bg={statusVariant} text={statusText as any} className='flex-shrink-0'>{campaign.status}</Badge>
                    </div>

                    {/* Channels */}
                    {enabledChannels.length > 0 && (
                      <div className='d-flex flex-wrap gap-1'>
                        {enabledChannels.map((ch) => (
                          <ChannelBadge key={ch} channel={ch as CampaignChannel} />
                        ))}
                      </div>
                    )}

                    {/* Stats */}
                    <div className='d-flex gap-4 text-center'>
                      {[
                        { label: 'Targets', value: campaign.totalTargets, color: 'text-primary' },
                        { label: 'Sent', value: campaign.sentCount, color: 'text-info' },
                        { label: 'Delivered', value: campaign.deliveredCount, color: 'text-success' },
                        { label: 'Failed', value: campaign.failedCount, color: 'text-danger' },
                      ].map((s) => (
                        <div key={s.label}>
                          <div className={clsx('fw-bold fs-4', s.color)}>{s.value ?? 0}</div>
                          <div className='text-muted fs-8'>{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Delivery rate bar */}
                    {deliveryRate !== null && (
                      <div>
                        <div className='d-flex justify-content-between small text-muted mb-1'>
                          <span>Delivery rate</span>
                          <span>{deliveryRate}%</span>
                        </div>
                        <div className='progress' style={{ height: 6 }}>
                          <div
                            className={clsx(
                              'progress-bar',
                              deliveryRate >= 80 ? 'bg-success' : deliveryRate >= 50 ? 'bg-warning' : 'bg-danger'
                            )}
                            style={{ width: `${deliveryRate}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className='d-flex justify-content-between align-items-center mt-auto pt-2 border-top text-muted small'>
                      <span className='text-uppercase fw-semibold'>{campaign.scheduleType}</span>
                      {lastRun && <span>Last run: {lastRun}</span>}
                    </div>
                  </div>
                </div>
              </Col>
            )
          })}
        </Row>
      )}

      <CampaignFormModal
        show={campaignModalOpen}
        onHide={() => setCampaignModalOpen(false)}
        onSubmit={handleSubmitCampaign}
        templates={templates}
        loading={creatingCampaign || updatingCampaign}
        campaign={editingCampaign}
      />
    </div>
  )
}
